import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Truck, RefreshCw, XCircle, MessageCircle } from "lucide-react";
import { WHATSAPP_NUMBER } from "@/lib/api";

const Section = ({ icon: Icon, title, children }) => (
  <section className="bg-white rounded-3xl border border-[#F4D8E2] p-6 shadow-[0_4px_20px_rgba(216,27,96,0.05)]">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-full bg-[#FCE4EC] text-[#D81B60] flex items-center justify-center flex-shrink-0"><Icon size={18} /></div>
      <h2 className="font-serif text-2xl text-[#2D0A22]">{title}</h2>
    </div>
    <div className="text-sm text-[#8E6377] leading-relaxed space-y-3">{children}</div>
  </section>
);

export const PoliciesPage = () => {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-24 md:pb-16 space-y-4" data-testid="policies-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#8E6377] hover:text-[#D81B60] mb-2">
        <ArrowLeft size={16} /> Back home
      </Link>

      <div className="mb-2">
        <p className="text-xs uppercase tracking-[0.2em] text-[#D81B60] font-bold">Our Promise</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D0A22] mt-1 leading-tight">Cancellation, Refund &amp; Return Policy</h1>
        <p className="mt-2 text-[#8E6377]">Handcrafted flowers made with love — we want every bouquet to arrive perfectly. Here's how we handle changes, refunds and rare damages.</p>
      </div>

      <Section icon={XCircle} title="Cancellation Policy">
        <p><b className="text-[#2D0A22]">Before dispatch:</b> You can cancel your order any time before it's shipped, straight from the <Link to="/track" className="text-[#D81B60] underline font-semibold">order tracking page</Link>. No questions asked.</p>
        <p><b className="text-[#2D0A22]">After dispatch:</b> Once your bouquet leaves our Vita workshop, we're unable to cancel the order because it's already in transit with our courier partner (Delhivery).</p>
        <p className="text-xs text-[#8E6377]">If you need urgent help, ping us on WhatsApp at <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="text-[#D81B60] underline">+91 93226 30051</a> — we'll do our best.</p>
      </Section>

      <Section icon={RefreshCw} title="Refunds (Prepaid Orders)">
        <p><b className="text-[#2D0A22]">Auto-refund:</b> When you cancel a prepaid Razorpay order before dispatch, we initiate the refund automatically to your original payment method. It typically reflects in <b>5-7 business days</b>.</p>
        <p><b className="text-[#2D0A22]">Manual refund:</b> In the rare case an auto-refund fails, our team will process it manually within <b>3 business days</b> and notify you on WhatsApp.</p>
        <p><b className="text-[#2D0A22]">COD orders:</b> No refund is required — you haven't been charged.</p>
      </Section>

      <Section icon={Truck} title="Post-Dispatch Return Policy">
        <p>Because every bouquet is handcrafted to order, we do not accept returns or exchanges once the item has shipped, <b>except</b> in the following cases:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>The parcel arrives visibly damaged in transit.</li>
          <li>The product is materially different from what was ordered.</li>
          <li>The product has a manufacturing defect (loose stitching, missing element, wrong colour).</li>
        </ul>
        <p><b className="text-[#2D0A22]">How to raise a claim:</b> Message us on WhatsApp within <b>48 hours of delivery</b> with 2-3 clear photos of the issue and your order ID. We'll offer a free replacement or full refund at our discretion.</p>
        <p className="text-xs text-[#8E6377]">Custom-designed bouquets (created via <Link to="/custom" className="text-[#D81B60] underline">Design Your Own</Link>) are non-returnable unless damaged or defective, as they're made specifically for you.</p>
      </Section>

      <Section icon={Shield} title="Delivery &amp; Timelines">
        <p>Standard delivery across India: <b>3-6 business days</b> from dispatch. Metro cities (Mumbai, Pune, Bengaluru, Delhi, Hyderabad, Chennai) usually arrive in 2-4 days.</p>
        <p>You'll receive a WhatsApp update the moment your order is dispatched, along with a Delhivery tracking link.</p>
      </Section>

      <Section icon={MessageCircle} title="Still have questions?">
        <p>We're a small handcraft team in Vita, Sangli — reach us directly on WhatsApp:</p>
        <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-2 px-5 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold">
          <MessageCircle size={16} /> Chat on WhatsApp
        </a>
      </Section>
    </div>
  );
};
